/**
 * Tableau du spectateur : l'œuvre entiere, la retouche en cours de construction,
 * et la vue libre.
 *
 * Il remplace un rendu FIGE de taille fixe. Regarder quelqu'un dessiner dans un
 * carre de 420 px sans pouvoir s'en approcher, c'est regarder une vignette : la
 * zone fait 10 % du tableau, donc quelques dizaines de pixels, et le detail qui
 * fait tout l'interet de la retouche y est invisible.
 *
 * Le moteur est celui de la chasse (`HuntEngine`), avec UN seul slot et le clic
 * desarme : il sait deja composer une retouche dans l'œuvre, deplacer la vue,
 * zoomer a la molette et au pincement. En ecrire un second n'aurait rien
 * apporte.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import type { DrawZone } from '@retusch/shared';
import { HuntEngine } from './HuntEngine.js';
import { loadArtwork } from './artwork.js';
import { observeSize } from './canvas/observeSize.js';

/** Slot unique. Sa valeur n'a aucune importance, seule son unicite compte. */
const ONLY_SLOT = 0;

export function SpectateBoard({
  file,
  zone,
  palette,
  drawing,
}: {
  readonly file: string;
  readonly zone: DrawZone;
  readonly palette: readonly string[];
  readonly drawing: ArrayBuffer | null;
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<HuntEngine | null>(null);

  /**
   * Cle STABLE de la zone, et non l'objet lui-meme.
   *
   * Le message `spectate` revient a chaque fois que la palette de l'auteur
   * s'enrichit, c'est-a-dire plusieurs fois par manche, et il apporte un objet
   * `zone` NEUF a chaque fois : meme valeurs, autre identite. Dependre de
   * l'objet reconstruisait donc le moteur en pleine observation, et le
   * spectateur voyait son zoom sauter au cadrage d'origine sans avoir rien
   * fait. C'est le meme piege que `HuntBoard` memorisant ses slots sur
   * `room.players` (cf. CLAUDE.md).
   */
  const zoneKey = `${zone.x},${zone.y},${zone.width},${zone.height}`;
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  /**
   * Le moteur ne depend QUE du tableau et de la zone.
   *
   * Surtout pas du dessin : il change toutes les deux secondes, et remonter le
   * moteur a chaque fois reprendrait au spectateur le cadrage qu'il vient de
   * choisir. C'est la meme regle que dans `HuntBoard`, ou memoriser sur
   * `room.players` recadrait l'ecran de tout le monde a chaque trouvaille.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    let alive = true;
    let stopResize: (() => void) | undefined;

    void loadArtwork(file)
      .then((image) => {
        if (!alive) return;
        const engine = new HuntEngine({
          canvas,
          image,
          slots: [
            {
              slot: ONLY_SLOT,
              drawerId: '',
              zone: zoneRef.current,
              palette,
              drawing,
              // `own` retire la retouche des cibles cliquables. Le spectateur
              // ne cherche rien, mais le dire ici evite d'en dependre.
              own: true,
              label: '',
            },
          ],
          // On REGARDE, on ne joue pas : aucun clic ne doit rien declencher.
          onStrike: () => undefined,
          canStrike: () => false,
        });
        engineRef.current = engine;
        engine.start();
        // La taille du canvas change sans evenement de fenetre (liste des
        // joueurs qui s'allonge, barre d'URL du telephone) : on observe la boite.
        stopResize = observeSize(canvas, () => engine.resize());
      })
      .catch(() => undefined);

    return () => {
      alive = false;
      stopResize?.();
      engineRef.current?.destroy();
      engineRef.current = null;
    };
    // `palette` et `drawing` sont volontairement absents : ils suivent par
    // `setDrawing`, sans toucher a la vue. `zone` passe par sa cle stable et
    // par une ref, pour la meme raison.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, zoneKey]);

  /** L'instantane suivant : la retouche avance, le cadrage ne bouge pas. */
  useEffect(() => {
    engineRef.current?.setDrawing(ONLY_SLOT, drawing, palette);
  }, [drawing, palette]);

  return <canvas ref={canvasRef} className="board__canvas board__canvas--grab" />;
}
